<template>
  <section id="step3" class="p-content-plan-manual-step">
    <div class="l-container">
      <h2 class="c-title--x-large">
        STEP3 記事の見出し構成案を設計する
      </h2>
      <section id="step3-1" class="p-content-plan-manual-step--sub">
        <h3 class="c-title--large">
          STEP3-1 上位表示サイトを参考にh1とh2を設定する
        </h3>
        <div class="l-rows">
          <p>
            h1は該当記事の中表紙、h2は目次にあたる部分となります。下記のポイントを押さえつつ見出し構成案を設計しながら、並行してタイトル案を検討するのがおすすめです。
          </p>
          <div class="c-panel">
            <p class="c-panel__title">ポイント</p>
            <ol class="c-list--decimal c-list-marker--bold l-rows--small">
              <li>
                <p class="c-text--bold">
                  ユーザーの検索意図を抜け漏れなく満たせるようh1→h2→h3…の順に組み立てる（ユーザー：無料で利用できるSEOツールを探している経験の浅い担当者）
                </p>
                <p class="c-text--small c-text--note">
                  上位サイトの見出しを参考に、記事をどのような内容や構成にすべきかを見出しを組み立てながら検討しましょう。また、上位サイトの見出しの個数を参考にすると、どの粒度の情報がどのくらい書かれているかを大まかに把握できます。
                </p>
              </li>
              <li>
                <p class="c-text--bold">
                  シナリオを参考にユーザーが自然に読み進められる順番に並べる
                </p>
                <p class="c-text--small c-text--note">
                  抜け漏れなくカバーできたとしても、順番が考慮されていないとユーザーは読みにくく感じ、途中で離脱してしまいます。STEP2で設計したシナリオに沿って、まずはh2で大きな流れを作っていきましょう。
                </p>
              </li>
            </ol>
          </div>
        </div>
        <section
          class="p-content-plan-manual-heading-count l-section"
          ref="headingCount"
        >
          <h4 class="c-title--medium" ref="headingCountTitle">
            見出し(h1～h3)の個数
          </h4>
        </section>
        <section class="l-section">
          <h4 class="c-title--medium">
            「{{ keyword }}」で1位～10位サイトのh1タグ一覧
          </h4>
          <div class="p-content-plan-manual-tdh-words">
            <ListContentManualText :tdhWordsItems="tdhWordsItems" scope="h1" />
            <section>
              <h5 class="c-title--small">使用頻度の高いキーワード</h5>
              <ListContentManualKeyword
                :tdhWordsItems="tdhWordsItems"
                scope="h1"
              />
            </section>
          </div>
        </section>
        <section class="l-section">
          <h4 class="c-title--medium">
            「{{ keyword }}」で1位～10位サイトのh2タグ一覧
          </h4>
          <div class="p-content-plan-manual-tdh-words">
            <ListContentManualText :tdhWordsItems="tdhWordsItems" scope="h2" />
            <section>
              <h5 class="c-title--small">使用頻度の高いキーワード</h5>
              <ListContentManualKeyword
                :tdhWordsItems="tdhWordsItems"
                scope="h2"
              />
            </section>
          </div>
        </section>
        <section class="l-section">
          <h4 class="c-title--medium">
            よくある質問
          </h4>
          <div class="l-rows">
            <div class="c-panel l-rows--small">
              <p>Q. 文字数と順位は関係ある？</p>
              <p>
                A.
                <strong
                  >直接的に関係はなく、あくまで「ユーザーの検索意図を満たしたページ」が上位表示されます。</strong
                >上位サイトの文字数は、あくまで参考程度に留めましょう。
              </p>
              <p class="c-text--small c-text--note">
                たくさんご質問をいただくので弊社で実験を行いました。<br />
                結果、文字数が多い＝一見情報が充実しているように見えるサイトが上位表示されるわけではなく、ユーザーの検索意図を抜け漏れなく満たしている質の高い記事が上位表示される、ということが分かりました。
              </p>
            </div>
            <div class="c-panel l-rows--small">
              <p>Q. 関連キーワードの含有率と順位は関係ある？</p>
              <p>
                A.
                <strong
                  >直接的な相関関係はありません。ユーザーの検索意図を満たそうとすると必然的に関連キーワードを使うことになる</strong
                >ため、結果、上位サイトが関連キーワードを多く含んでいるように見える場合はあります。
              </p>
              <p class="c-text--small c-text--note">
                こちらも同様にたくさんご質問をいただくので弊社で実験を行いました。<br />
                無作為に多くのキーワードを含めた記事が上位表示されることはなく、関連キーワードの含有率と順位に直接的な相関関係はありませんでした。関連キーワードは無理やり記事に含めようとするのではなく、ユーザーの検索意図を満たすために使用するとよい表現の一例と考え、あくまで必要に応じて含める程度にしましょう。
              </p>
            </div>
          </div>
        </section>
      </section>
      <section id="step3-2" class="p-content-plan-manual-step--sub">
        <h3 class="c-title--large">
          STEP3-2 独自情報を追加する
        </h3>
        <div class="l-rows">
          <p>
            独自に収集したデータや筆者の考察、導入事例など、貴社にしか提供できない情報を追加しましょう。
          </p>
          <div class="c-panel">
            <p class="c-panel__title">独自情報の例</p>
            <ul class="c-list--disc c-text--bold">
              <li>自社で独自に行った実験や調査の結果</li>
              <li>有識者に取材した内容</li>
              <li>実際のお客様の声</li>
              <li>商品の導入事例・成功事例</li>
              <li>記事執筆者の考察</li>
            </ul>
          </div>
          <p>
            ※独自情報の追加には、比較的工数がかかります。そのため、すぐに掲載できる独自情報がない場合は記事の新規公開時点では含めず、リライト時に追加で執筆いただければ問題ございません。
          </p>
        </div>
      </section>
    </div>
  </section>
</template>
<script>
import ListContentManualText from '@/components/contentPlans/ListContentManualText';
import ListContentManualKeyword from '@/components/contentPlans/ListContentManualKeyword';
export default {
  props: {
    keyword: {
      type: String,
      required: true
    },
    project: {
      type: Object,
      required: true
    },
    tdhWordsItems: {
      type: Array,
      required: true
    }
  },
  components: {
    ListContentManualText,
    ListContentManualKeyword
  },
  mounted() {
    this.appendHeadingCount();
  },
  methods: {
    appendHeadingCount() {
      // 見出しの個数のDOMを挿入する
      if (this.$refs.headingCount && this.$refs.headingCountTitle) {
        const block = document
          .querySelector('[data-include-manual-download="headingCount"]')
          .cloneNode(true);

        // 見出しレベルをh3からh4にしたくて置き換えている
        const h3 = block.querySelector('.l-block__header');
        h3.parentNode.replaceChild(this.$refs.headingCountTitle, h3);

        this.$refs.headingCount.appendChild(block);
      }
    }
  }
};
</script>
